import React, { useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';

const PRINT_CLASS = 'elite-generated-document-manual-print';
const MANUAL_PAGE_STYLE = '@page { size: Letter portrait; margin: 0 !important; }';

// Proof PDF thumbnails can be rendered asynchronously after the document is
// otherwise ready. Avoid capturing a white placeholder in Save as PDF.
const waitForProofThumbnails = async () => {
  const isLoading = () => Array.from(document.querySelectorAll('.proof-review-card__preview'))
    .some((preview) => /loading\.\.\./i.test(preview.textContent || ''));
  if (!isLoading() || !document.body || typeof MutationObserver === 'undefined') return;

  await new Promise((resolve) => {
    let timeoutId;
    const finish = () => {
      observer.disconnect();
      window.clearTimeout(timeoutId);
      resolve();
    };
    const observer = new MutationObserver(() => {
      if (!isLoading()) finish();
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    timeoutId = window.setTimeout(finish, 8000);
    if (!isLoading()) finish();
  });
};

const waitForAssets = async () => {
  if (document.fonts?.ready) {
    await Promise.race([
      document.fonts.ready.catch(() => {}),
      new Promise((resolve) => setTimeout(resolve, 8000)),
    ]);
  }
  await waitForProofThumbnails();
  const images = Array.from(document.querySelectorAll('img'));
  await Promise.race([
    Promise.all(images.map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    })),
    new Promise((resolve) => setTimeout(resolve, 10000)),
  ]);
};

// Print the displayed snapshot in place: never navigate, regenerate a document,
// or open a second tab. Scope the invoice-style print adjustments to this action,
// so the automatic Puppeteer snapshot PDF renderer is unchanged.
export const printCurrentGeneratedDocument = async () => {
  await waitForAssets();
  if (!document.body || !document.head) return;

  const style = document.createElement('style');
  style.setAttribute('data-generated-document-manual-page', 'true');
  style.textContent = MANUAL_PAGE_STYLE;
  document.head.appendChild(style);
  // The document route keeps this class mounted for native Ctrl+P. Only
  // remove it here when this standalone print action was its owner.
  const hadPrintLayout = document.body.classList.contains(PRINT_CLASS);
  document.body.classList.add(PRINT_CLASS);

  let cleanedUp = false;
  let fallbackTimer;
  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    window.clearTimeout(fallbackTimer);
    window.removeEventListener('afterprint', cleanup);
    if (!hadPrintLayout) document.body.classList.remove(PRINT_CLASS);
    style.remove();
  };

  window.addEventListener('afterprint', cleanup);
  // Some browsers omit afterprint when the dialog is cancelled.
  fallbackTimer = window.setTimeout(cleanup, 60000);

  try {
    // Allow the temporary print stylesheet to be applied before Chrome
    // snapshots this document for its system Print / Save as PDF dialog.
    await new Promise((resolve) => {
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(resolve);
      } else {
        resolve();
      }
    });
    window.print();
  } catch (error) {
    cleanup();
    throw error;
  }
};

const GeneratedDocumentPrintActions = ({
  isRenderMode = false,
  isLoggedIn = false,
  ready = false,
  bottomLeftOffset = 16,
}) => {
  const [preparing, setPreparing] = useState(false);

  if (!isLoggedIn || isRenderMode || !ready) return null;

  const handlePrint = async () => {
    if (preparing) return;
    setPreparing(true);
    try {
      await printCurrentGeneratedDocument();
    } catch (error) {
      console.error('[GeneratedDocumentPrint] Unable to open print dialog:', error);
    } finally {
      setPreparing(false);
    }
  };

  return (
    <Box
      data-generated-document-print-actions
      sx={{
        position: 'fixed',
        bottom: 16,
        left: bottomLeftOffset,
        zIndex: 2000,
        '@media print': { display: 'none !important' },
      }}
    >
      <Tooltip title="Print / Save as PDF">
        <span>
          <IconButton
            size="large"
            aria-label="Print / Save as PDF"
            disabled={preparing}
            onClick={handlePrint}
            sx={{
              bgcolor: '#263b55',
              color: '#fff',
              border: '1px solid #172c47',
              borderRadius: '50%',
              boxShadow: 2,
              '&:hover': { bgcolor: '#172c47', boxShadow: 4 },
              '&.Mui-disabled': { bgcolor: '#263b55', color: '#fff' },
            }}
          >
            {preparing ? <CircularProgress size={24} color="inherit" /> : <PrintIcon />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default GeneratedDocumentPrintActions;
